// ── /join/:token ────────────────────────────────────────────────────────────────
// Where a scanned group QR (or a shared invite link) lands. Shows a deliberately narrow
// preview of the group, then either joins directly or walks the visitor through the
// pay-then-join step when the group charges an entry fee.
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { useAuth } from '../context/AuthContext';
import {
  GROUP_BY_JOIN_TOKEN_QUERY, JOIN_GROUP_BY_TOKEN_MUTATION,
  REQUEST_GROUP_JOIN_PAYMENT_MUTATION, REPORT_GROUP_JOIN_PAYMENT_MUTATION,
} from '../graphql';
import UpiPayPanel from '../components/UpiPayPanel';
import { groupCover } from '../config/groupImages';

const rupees = (paise) => `₹${(paise / 100).toFixed(2)}`;

export default function JoinByToken({ onAuthRequired }) {
  const { token } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [error, setError] = useState('');
  const [payment, setPayment] = useState(null);   // the pay-to-join panel, once requested
  const [upiRef, setUpiRef] = useState('');
  const [reported, setReported] = useState(false);

  const { data, loading, refetch } = useQuery(GROUP_BY_JOIN_TOKEN_QUERY, {
    variables: { token }, fetchPolicy: 'network-only',
  });
  const preview = data?.groupByJoinToken;

  const [joinByToken, { loading: joining }] = useMutation(JOIN_GROUP_BY_TOKEN_MUTATION, {
    refetchQueries: ['Me'],
  });
  const [requestPayment, { loading: requestingPayment }] = useMutation(REQUEST_GROUP_JOIN_PAYMENT_MUTATION);
  const [reportPayment, { loading: reportingPayment }] = useMutation(REPORT_GROUP_JOIN_PAYMENT_MUTATION);

  // Re-check the preview once the visitor signs in, so `alreadyMember` is accurate for them.
  useEffect(() => { if (currentUser) refetch(); }, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleJoin = async () => {
    setError('');
    if (!currentUser) { onAuthRequired?.('register'); return; }
    try {
      const res = await joinByToken({ variables: { token } });
      navigate(`/group/${res.data.joinGroupByToken.id}`);
    } catch (e) {
      // The server tells us a fee is outstanding via PAYMENT_REQUIRED — switch to the payment step
      // rather than showing a dead end.
      if (e.graphQLErrors?.[0]?.extensions?.code === 'PAYMENT_REQUIRED') {
        await openPayment();
        return;
      }
      setError(e.message);
    }
  };

  const openPayment = async () => {
    setError('');
    if (!currentUser) { onAuthRequired?.('register'); return; }
    try {
      // Pass the scanned token so the admin can see which QR brought this payment in. This page is
      // the only place the token is known at payment time.
      const res = await requestPayment({ variables: { groupId: preview.groupId, token } });
      setPayment(res.data.requestGroupJoinPayment);
      if (res.data.requestGroupJoinPayment.existing) setReported(true);
    } catch (e) { setError(e.message); }
  };

  const handleReport = async () => {
    setError('');
    try {
      const res = await reportPayment({ variables: { groupId: preview.groupId, upiRef: upiRef.trim() || null, token } });
      // Auto-admit means they're in already — take them to the group instead of telling them to wait.
      if (res.data?.reportGroupJoinPayment?.admitted) { navigate(`/group/${preview.groupId}`); return; }
      setReported(true);
    } catch (e) { setError(e.message); }
  };

  if (loading) {
    return <div className="container" style={{ paddingTop: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading invitation…</div>;
  }

  if (!preview) {
    return (
      <div className="container" style={{ paddingTop: 40, maxWidth: 520 }}>
        <div className="card" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 44, marginBottom: 8 }}>🔗</div>
          <h2 style={{ fontSize: 'var(--font-lg)', fontWeight: 800, marginBottom: 8 }}>This link isn’t valid</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: 'var(--font-sm)', marginBottom: 16 }}>
            The join link may have been mistyped or removed. Ask the group admin for a fresh one.
          </p>
          <button className="btn btn-primary" onClick={() => navigate('/')}>Browse groups</button>
        </div>
      </div>
    );
  }

  const art = groupCover({ category: preview.category, broadCategory: preview.isEvent ? 'culture' : '', imageUrl: preview.imageUrl });
  const where = [preview.building, preview.city].filter(Boolean).join(' · ');

  return (
    <div className="container" style={{ paddingTop: 32, paddingBottom: 40, maxWidth: 560 }}>
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {/* Cover */}
        <div style={{ height: 140, background: preview.imageUrl ? `url(${preview.imageUrl}) center/cover` : art.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {!preview.imageUrl && <span style={{ fontSize: 56 }}>{art.emoji}</span>}
        </div>

        <div style={{ padding: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--primary)', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 6 }}>
            {preview.isEvent ? '🎭 You’re invited to an event' : '👥 You’re invited to join'}
          </div>
          <h1 style={{ fontSize: 'var(--font-xl)', fontWeight: 800, marginBottom: 6 }}>{preview.name}</h1>
          {where && <div style={{ fontSize: 'var(--font-sm)', color: 'var(--text-muted)', marginBottom: 10 }}>📍 {where}</div>}
          {preview.description && (
            <p style={{ fontSize: 'var(--font-sm)', color: 'var(--text-muted)', marginBottom: 14, lineHeight: 1.5 }}>{preview.description}</p>
          )}
          <div style={{ display: 'flex', gap: 14, fontSize: 'var(--font-sm)', color: 'var(--text-muted)', marginBottom: 18, flexWrap: 'wrap' }}>
            <span>👥 {preview.memberCount}/{preview.maxMembers} members</span>
            {preview.joinFeePaise > 0 && <span style={{ fontWeight: 700, color: 'var(--text)' }}>💰 {rupees(preview.joinFeePaise)} to join</span>}
          </div>

          {/* Link state problems come first — no point offering a button that cannot work. */}
          {!preview.valid && (
            <div style={{ padding: '12px 14px', borderRadius: 'var(--radius-sm)', background: 'rgba(220,38,38,0.08)', color: 'var(--danger)', fontSize: 'var(--font-sm)', fontWeight: 600, marginBottom: 14 }}>
              {preview.invalidReason}
            </div>
          )}

          {preview.alreadyMember ? (
            <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => navigate(`/group/${preview.groupId}`)}>
              ✅ You’re already a member — open the group
            </button>
          ) : preview.isFull ? (
            <div style={{ padding: '12px 14px', borderRadius: 'var(--radius-sm)', background: 'var(--surface2)', color: 'var(--text-muted)', fontSize: 'var(--font-sm)', textAlign: 'center' }}>
              This group is full. Ask the admin to make room.
            </div>
          ) : !preview.valid ? null : payment ? (
            <PaymentStep
              payment={payment} reported={reported} upiRef={upiRef} setUpiRef={setUpiRef}
              onReport={handleReport} reporting={reportingPayment}
              onRetryJoin={handleJoin} joining={joining} note={preview.joinFeeNote}
            />
          ) : (
            <>
              <button className="btn btn-primary" style={{ width: '100%' }} disabled={joining || requestingPayment}
                onClick={preview.joinFeePaise > 0 ? openPayment : handleJoin}>
                {joining || requestingPayment ? 'Working…'
                  : preview.joinFeePaise > 0 ? `💰 Pay ${rupees(preview.joinFeePaise)} & join`
                  : currentUser ? '🎉 Join this group' : '🎉 Sign up & join'}
              </button>
              {!currentUser && (
                <p style={{ fontSize: 12, color: 'var(--text-dim)', textAlign: 'center', marginTop: 10 }}>
                  You can sign up with your mobile number — no password needed.
                </p>
              )}
            </>
          )}

          {error && (
            <div style={{ marginTop: 12, padding: '10px 12px', borderRadius: 'var(--radius-sm)', background: 'rgba(220,38,38,0.08)', color: 'var(--danger)', fontSize: 'var(--font-sm)' }}>{error}</div>
          )}
        </div>
      </div>
    </div>
  );
}

// The pay-then-join step. Payment happens in the visitor's own UPI app, so all this screen can do is
// make paying easy and record the claim — the admin confirms receipt before membership is granted.
function PaymentStep({ payment, reported, upiRef, setUpiRef, onReport, reporting, onRetryJoin, joining, note }) {
  if (reported) {
    return (
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 40, marginBottom: 8 }}>⏳</div>
        <div style={{ fontWeight: 700, marginBottom: 6 }}>Payment reported</div>
        <p style={{ fontSize: 'var(--font-sm)', color: 'var(--text-muted)', marginBottom: 16 }}>
          The group admin has been notified. You’ll get a notification the moment they confirm it,
          and you’ll be added automatically.
        </p>
        <button className="btn btn-ghost btn-sm" onClick={onRetryJoin} disabled={joining}>
          {joining ? 'Checking…' : 'Already confirmed? Try joining'}
        </button>
      </div>
    );
  }

  return (
    <UpiPayPanel
      vpa={payment.upiVpa}
      payeeName={payment.payeeName}
      amountPaise={payment.amountPaise}
      note={note || payment.note}
      upiUri={payment.upiUri}
      qrImage={payment.upiQrImage}
    >
      <div className="form-group" style={{ textAlign: 'left' }}>
        <label className="form-label">UPI reference number (optional, helps the admin match it)</label>
        <input className="input" placeholder="e.g. 431298765432" value={upiRef} onChange={e => setUpiRef(e.target.value)} />
      </div>

      <button className="btn btn-primary" style={{ width: '100%' }} onClick={onReport} disabled={reporting}>
        {reporting ? 'Reporting…' : '✅ I’ve paid'}
      </button>
      <p style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 8 }}>
        Once you've paid, tap “I’ve paid”. The admin confirms receipt and you’re in.
      </p>
    </UpiPayPanel>
  );
}
